import React, { useState } from 'react';
import { Button, Form, Spinner, Stack } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPen, faTrash } from '@fortawesome/free-solid-svg-icons';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { toast } from 'react-toastify';
import { useAuth } from 'hooks/useAuth';
import axiosInstance from 'utils/axiosInterceptors';
import { hasAnyRole } from 'utils/roleAccess';
import { toastConfig } from 'utils/toastConfig';

type ApiErrorBody = {
  errorMessage?: string;
};

type Props = {
  branchDomain: string;
  startingCash: number;
};

const formatStartingCash = (value: number) =>
  `₱${value.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })}`;

const errorMessage = (error: unknown, fallback: string) => {
  if (
    error instanceof AxiosError &&
    (error.response?.status === 401 || error.response?.status === 403)
  ) {
    return 'Only an admin can change the starting cash.';
  }
  if (error instanceof AxiosError) {
    return (
      (error.response?.data as ApiErrorBody | undefined)?.errorMessage ||
      fallback
    );
  }
  return fallback;
};

const StartingCashBanner = ({ branchDomain, startingCash }: Props) => {
  const { token } = useAuth();
  const queryClient = useQueryClient();
  const isAdmin = hasAnyRole(token?.roles, ['Admin']);
  const [editing, setEditing] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [amount, setAmount] = useState(String(startingCash));

  const refresh = async () => {
    await queryClient.invalidateQueries({
      queryKey: ['storeDay', branchDomain]
    });
  };

  const updateCash = useMutation({
    mutationFn: async (nextAmount: number) => {
      await axiosInstance.put(`/api/v1/${branchDomain}/storeDays/today`, {
        startingCash: nextAmount
      });
    },
    onSuccess: async () => {
      setEditing(false);
      await refresh();
    },
    onError: (error: unknown) => {
      toast.error(errorMessage(error, 'Could not update the starting cash.'), toastConfig);
    }
  });

  const removeCash = useMutation({
    mutationFn: async () => {
      await axiosInstance.delete(`/api/v1/${branchDomain}/storeDays/today`);
    },
    onSuccess: async () => {
      setConfirmRemove(false);
      await refresh();
    },
    onError: (error: unknown) => {
      toast.error(errorMessage(error, 'Could not remove the starting cash.'), toastConfig);
    }
  });

  const save = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const nextAmount = Number(amount);
    if (amount.trim() === '' || !Number.isFinite(nextAmount) || nextAmount < 0) {
      toast.error('Enter a starting cash amount of zero or more.', toastConfig);
      return;
    }
    updateCash.mutate(nextAmount);
  };

  return (
    <div className="mb-3">
      <Stack direction="horizontal" gap={2} className="align-items-start flex-wrap">
        <div>
          <span className="text-body-tertiary text-uppercase fs-10">
            Starting Cash
          </span>
          {!editing && (
            <Stack direction="horizontal" gap={2} className="align-items-center">
              <div className="fs-6 fw-semibold">{formatStartingCash(startingCash)}</div>
              {isAdmin && !confirmRemove && (
                <>
                  <Button
                    variant="link"
                    className="p-0 text-body-tertiary"
                    aria-label="Update starting cash"
                    onClick={() => {
                      setAmount(String(startingCash));
                      setEditing(true);
                    }}
                  >
                    <FontAwesomeIcon icon={faPen} />
                  </Button>
                  <Button
                    variant="link"
                    className="p-0 text-body-tertiary"
                    aria-label="Remove starting cash"
                    onClick={() => setConfirmRemove(true)}
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </Button>
                </>
              )}
            </Stack>
          )}
        </div>
      </Stack>

      {isAdmin && editing && (
        <Form className="mt-2" onSubmit={save}>
          <Stack direction="horizontal" gap={2} className="align-items-center flex-wrap">
            <Form.Control
              type="number"
              min={0}
              step="0.01"
              inputMode="decimal"
              value={amount}
              autoFocus
              style={{ maxWidth: '12rem' }}
              onChange={event => setAmount(event.target.value)}
            />
            <Button
              type="submit"
              size="sm"
              variant="primary"
              disabled={updateCash.isPending}
            >
              {updateCash.isPending ? <Spinner animation="border" size="sm" /> : 'Save'}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="phoenix-secondary"
              disabled={updateCash.isPending}
              onClick={() => setEditing(false)}
            >
              Cancel
            </Button>
          </Stack>
        </Form>
      )}

      {isAdmin && confirmRemove && (
        <Stack direction="horizontal" gap={2} className="mt-2 align-items-center flex-wrap">
          <span className="fs-9 text-body-tertiary">
            Remove today’s starting cash?
          </span>
          <Button
            size="sm"
            variant="danger"
            disabled={removeCash.isPending}
            onClick={() => removeCash.mutate()}
          >
            {removeCash.isPending ? <Spinner animation="border" size="sm" /> : 'Remove'}
          </Button>
          <Button
            size="sm"
            variant="phoenix-secondary"
            disabled={removeCash.isPending}
            onClick={() => setConfirmRemove(false)}
          >
            Cancel
          </Button>
        </Stack>
      )}
    </div>
  );
};

export default StartingCashBanner;
